import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faChevronDown, 
    faChevronUp,
    faUser,
    faCalendar,
    faComment,
    faClipboardList,
    faPaperPlane,
    faCheckCircle,
    faSync,
    faEdit,
    faPlus,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import { AuthContext } from '../../context/AuthContext';
import { smartCase } from '../../utils/smartCase';
import './DeliveryLogsModal.css';

/**
 * DeliveryLogsModal Component
 * 
 * Displays audit logs and comments history for a delivery.
 * Shows actions like: Created, Updated, Activated, Comment Added
 * 
 * For delivery reviewers, includes a comment input inside each log card body.
 * 
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Callback to close the modal
 * - deliveryId: number - ID of the delivery
 * - deliveryNumber: string - Delivery number for display
 */
const DeliveryLogsModal = ({ isOpen, onClose, deliveryId, deliveryNumber }) => {
    const { user } = useContext(AuthContext);
    const [logs, setLogs] = useState({ audit_logs: [], comments: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedLogs, setExpandedLogs] = useState({});
    
    // Comment input state per log card (keyed by log id)
    const [commentTexts, setCommentTexts] = useState({});
    const [submittingComment, setSubmittingComment] = useState({});
    const [commentErrors, setCommentErrors] = useState({});
    const [commentSuccess, setCommentSuccess] = useState({});
    
    // Check if current user is a delivery reviewer
    const isReviewer = user?.is_delivery_reviewer === true;
    
    // Debug logging
////    console.log('DeliveryLogsModal - Full user object:', user);
////    console.log('DeliveryLogsModal - is_delivery_reviewer value:', user?.is_delivery_reviewer, typeof user?.is_delivery_reviewer);
////    console.log('DeliveryLogsModal - isReviewer result:', isReviewer);
    
    useEffect(() => {
        if (isOpen && deliveryId) {
            fetchLogs();
            // Reset comment state when modal opens
            setCommentTexts({});
            setCommentErrors({});
            setCommentSuccess({});
            setSubmittingComment({});
        }
    }, [isOpen, deliveryId]);
    
    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/logs/`);
            setLogs(response.data);
            // Expand first log by default
            if (response.data.audit_logs?.length > 0) {
                setExpandedLogs({ [`audit-${response.data.audit_logs[0].id}`]: true });
            }
        } catch (err) {
            console.error('Error fetching delivery logs:', err);
            setError('Failed to load delivery logs');
        } finally {
            setLoading(false);
        }
    };
    
    // Handle comment text change for a specific log
    const handleCommentChange = (logId, text) => {
        setCommentTexts(prev => ({ ...prev, [logId]: text }));
        // Clear error when user starts typing
        if (commentErrors[logId]) {
            setCommentErrors(prev => ({ ...prev, [logId]: null }));
        }
    };
    
    // Handle comment submission for a specific log
    const handleSubmitComment = async (logId) => {
        const commentText = commentTexts[logId]?.trim();
        
        if (!commentText) {
            setCommentErrors(prev => ({ ...prev, [logId]: 'Please enter a comment' }));
            return;
        }
        
        setSubmittingComment(prev => ({ ...prev, [logId]: true }));
        setCommentErrors(prev => ({ ...prev, [logId]: null }));
        setCommentSuccess(prev => ({ ...prev, [logId]: false }));
        
        try {
            const response = await axiosInstance.post(`/api/deliveries/${deliveryId}/add-comment/`, {
                comment: commentText
            });
            
            if (response.data.success) {
                setCommentSuccess(prev => ({ ...prev, [logId]: true }));
                setCommentTexts(prev => ({ ...prev, [logId]: '' }));
                // Refresh logs to show the new comment
                await fetchLogs();
                // Clear success message after 3 seconds
                setTimeout(() => {
                    setCommentSuccess(prev => ({ ...prev, [logId]: false }));
                }, 3000);
            } else {
                setCommentErrors(prev => ({ ...prev, [logId]: response.data.message || 'Failed to add comment' }));
            }
        } catch (err) {
            console.error('Error adding comment:', err);
            setCommentErrors(prev => ({ 
                ...prev, 
                [logId]: err.response?.data?.message || 'Failed to add comment. Please try again.' 
            }));
        } finally {
            setSubmittingComment(prev => ({ ...prev, [logId]: false }));
        }
    };
    
    if (!isOpen) return null;
    
    // Toggle log expansion
    const toggleLog = (logId) => {
        setExpandedLogs(prev => ({
            ...prev,
            [logId]: !prev[logId]
        }));
    };
    
    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Get action icon
    const getActionIcon = (action) => {
        switch (action) {
            case 'CREATED':
                return faPlus;
            case 'UPDATED':
            case 'UPDATED_ACTIVE':
                return faEdit;
            case 'SUBMITTED_FOR_REVIEW':
            case 'ACTIVATED':
                return faPaperPlane;
            case 'COMMENT_ADDED':
            case 'COMMENT':
                return faComment;
            case 'REVIEWED':
                return faCheckCircle;
            case 'RESUBMITTED':
                return faSync;
            default:
                return faClipboardList;
        }
    };
    
    // Get action badge class
    const getActionBadgeClass = (action) => {
        switch (action) {
            case 'CREATED':
                return 'delivery-log-action-created';
            case 'UPDATED':
                return 'delivery-log-action-updated';
            case 'ACTIVATED':  // First submission
                return 'delivery-log-action-submitted';
            case 'UPDATED_ACTIVE':  // Resubmission
                return 'delivery-log-action-resubmitted';
            case 'SUBMITTED_FOR_REVIEW':
            case 'RESUBMITTED':
                return 'delivery-log-action-submitted';
            case 'COMMENT_ADDED':
            case 'COMMENT':
                return 'delivery-log-action-comment';
            case 'REVIEWED':
                return 'delivery-log-action-reviewed';
            default:
                return 'delivery-log-action-default';
        }
    };
    
    // Combine and sort logs with comments
    const getCombinedLogs = () => {
        const combinedLogs = [];
        
        // Add audit logs (filter out COMMENT_ADDED since we show comments separately)
        logs.audit_logs?.forEach(log => {
            // Skip COMMENT_ADDED audit logs - we already show the actual comment
            if (log.action === 'COMMENT_ADDED') return;
            
            combinedLogs.push({
                type: 'audit',
                id: `audit-${log.id}`,
                timestamp: log.timestamp,
                action: log.action,
                action_display: log.action_display,
                user_name: log.user_name,
                user_email: log.user_email,
                notes: log.notes,
                changes: log.changes
            });
        });
        
        // Add comments (as separate entries)
        logs.comments?.forEach(comment => {
            combinedLogs.push({
                type: 'comment',
                id: `comment-${comment.id}`,
                timestamp: comment.created_at,
                action: 'COMMENT',
                action_display: 'Comment',
                user_name: comment.commenter_name,
                user_email: comment.commenter_email,
                comment: comment.comment
            });
        });
        
        // Sort by timestamp (newest first)
        return combinedLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    };
    
    // Handle overlay click (close modal when clicking outside)
    const handleOverlayClick = (e) => {
        if (e.target.className === 'delivery-logs-overlay') {
            onClose();
        }
    };
    
    const combinedLogs = getCombinedLogs();
    
    return (
        <div className="delivery-logs-overlay" onClick={handleOverlayClick}>
            <div className="delivery-logs-modal">
                {/* Header */}
                <div className="delivery-logs-header">
                    <h3>
                        <FontAwesomeIcon icon={faClipboardList} /> Delivery Logs
                    </h3>
                    <p className="delivery-logs-delivery-number">{deliveryNumber || `Delivery #${deliveryId}`}</p>
                    <button 
                        className="delivery-logs-close-button" 
                        onClick={onClose}
                        aria-label="Close Delivery Logs"
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                {/* Body - Logs List */}
                <div className="delivery-logs-body">
                    {loading ? (
                        <div className="delivery-logs-loading">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                            <p>Loading logs...</p>
                        </div>
                    ) : error ? (
                        <div className="delivery-logs-error">
                            <p>{error}</p>
                            <button onClick={fetchLogs}>Retry</button>
                        </div>
                    ) : combinedLogs.length === 0 ? (
                        <div className="delivery-logs-empty">
                            <FontAwesomeIcon icon={faClipboardList} size="3x" />
                            <p>No logs available for this delivery</p>
                        </div>
                    ) : (
                        <div className="delivery-logs-list-container">
                            {combinedLogs.map((log, index) => {
                                const isExpanded = expandedLogs[log.id];
                                const isSubmitting = submittingComment[log.id];
                                const hasError = commentErrors[log.id];
                                const hasSuccess = commentSuccess[log.id];
                                const currentCommentText = commentTexts[log.id] || '';
                                
                                return (
                                    <div key={log.id} className="delivery-logs-card">
                                        {/* Log Card Header (Clickable) */}
                                        <div 
                                            className="delivery-logs-card-header"
                                            onClick={() => toggleLog(log.id)}
                                        >
                                            <div className="delivery-logs-card-header-left">
                                                <span className="delivery-logs-card-toggle-icon">
                                                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                                </span>
                                                <div className="delivery-logs-card-title">
                                                    <h4>
                                                        <FontAwesomeIcon icon={getActionIcon(log.action)} />
                                                        {' '}{log.action_display || log.action}
                                                    </h4>
                                                    <span className="delivery-logs-card-date">
                                                        {formatDate(log.timestamp)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="delivery-logs-card-header-right">
                                                <span className={`delivery-logs-action-badge ${getActionBadgeClass(log.action)}`}>
                                                    {log.type === 'comment' ? 'Comment' : log.action_display}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Log Card Body (Expandable) */}
                                        {isExpanded && (
                                            <div className="delivery-logs-card-body">
                                                {/* User Info */}
                                                <div className="delivery-logs-card-meta">
                                                    <div className="delivery-logs-meta-item">
                                                        <FontAwesomeIcon icon={faUser} />
                                                        <span>
                                                            <strong>By:</strong> {log.user_name || 'Unknown'}
                                                        </span>
                                                    </div>
                                                    {log.user_email && (
                                                        <div className="delivery-logs-meta-item">
                                                            <span>
                                                                <strong>Email:</strong> {log.user_email}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="delivery-logs-meta-item">
                                                        <FontAwesomeIcon icon={faCalendar} />
                                                        <span>
                                                            <strong>When:</strong> {formatDate(log.timestamp)}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                {/* Comment Content (for comment type) */}
                                                {log.type === 'comment' && log.comment && (
                                                    <div className="delivery-logs-comment-section">
                                                        <h5>
                                                            <FontAwesomeIcon icon={faComment} /> Comment
                                                        </h5>
                                                        <p className="delivery-logs-comment-text">{log.comment}</p>
                                                    </div>
                                                )}
                                                
                                                {/* Notes (for audit log type) */}
                                                {log.type === 'audit' && log.notes && (
                                                    <div className="delivery-logs-notes-section">
                                                        <h5>Notes</h5>
                                                        <p>{log.notes}</p>
                                                    </div>
                                                )}
                                                
                                                {/* Changed Fields Section - Detailed field changes */}
                                                {log.changes?.changed_fields && Object.keys(log.changes.changed_fields).length > 0 && (
                                                    <div className="delivery-logs-changed-fields-section">
                                                        <h5>Changed Fields</h5>
                                                        <div className="delivery-logs-changed-fields-list">
                                                            {Object.entries(log.changes.changed_fields).map(([fieldKey, fieldData]) => (
                                                                <div key={fieldKey} className="delivery-logs-changed-field-item">
                                                                    <span className="delivery-logs-field-label">
                                                                        {fieldData.label || fieldKey.replace(/_/g, ' ')}:
                                                                    </span>
                                                                    <span className="delivery-logs-field-old">
                                                                        {fieldData.old || '(empty)'}
                                                                    </span>
                                                                    <span className="delivery-logs-field-arrow">
                                                                        &rarr;
                                                                    </span>
                                                                    <span className="delivery-logs-field-new">
                                                                        {fieldData.new || '(empty)'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Other Details (non-field changes) - excludes changed_fields and hides redundant status */}
                                                {log.changes && Object.keys(log.changes).filter(key => {
                                                    // Exclude changed_fields (shown above)
                                                    if (key === 'changed_fields') return false;
                                                    // Hide status change if old_status == new_status
                                                    if (key === 'old_status' && log.changes.old_status === log.changes.new_status) return false;
                                                    if (key === 'new_status' && log.changes.old_status === log.changes.new_status) return false;
                                                    return true;
                                                }).length > 0 && (
                                                    <div className="delivery-logs-changes-section">
                                                        <h5>Details</h5>
                                                        <div className="delivery-logs-changes-list">
                                                            {Object.entries(log.changes)
                                                                .filter(([key]) => {
                                                                    // Exclude changed_fields (shown above)
                                                                    if (key === 'changed_fields') return false;
                                                                    // Hide status change if old_status == new_status
                                                                    if (key === 'old_status' && log.changes.old_status === log.changes.new_status) return false;
                                                                    if (key === 'new_status' && log.changes.old_status === log.changes.new_status) return false;
                                                                    return true;
                                                                })
                                                                .map(([key, value]) => (
                                                                    <div key={key} className="delivery-logs-change-item">
                                                                        <span className="delivery-logs-change-key">
                                                                            {smartCase(key.replace(/_/g, ' '))}:
                                                                        </span>
                                                                        <span className="delivery-logs-change-value">
                                                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Comment Input - Only for reviewers and audit logs */}
                                                {isReviewer && log.type === 'audit' && (
                                                    <div className="delivery-logs-inline-comment">
                                                        <div className="delivery-logs-inline-comment-header">
                                                            <FontAwesomeIcon icon={faComment} />
                                                            <span>Add Feedback</span>
                                                        </div>
                                                        
                                                        <div className="delivery-logs-inline-comment-input-row">
                                                            <textarea
                                                                className="delivery-logs-inline-textarea"
                                                                placeholder="Enter your review feedback..."
                                                                value={currentCommentText}
                                                                onChange={(e) => handleCommentChange(log.id, e.target.value)}
                                                                disabled={isSubmitting}
                                                                rows={2}
                                                            />
                                                            <button
                                                                className="delivery-logs-inline-send-btn"
                                                                onClick={() => handleSubmitComment(log.id)}
                                                                disabled={isSubmitting || !currentCommentText.trim()}
                                                                type="button"
                                                                title="Send feedback"
                                                            >
                                                                {isSubmitting ? (
                                                                    <FontAwesomeIcon icon={faSpinner} spin />
                                                                ) : (
                                                                    <FontAwesomeIcon icon={faPaperPlane} />
                                                                )}
                                                            </button>
                                                        </div>
                                                        
                                                        {hasError && (
                                                            <div className="delivery-logs-inline-error">
                                                                {hasError}
                                                            </div>
                                                        )}
                                                        
                                                        {hasSuccess && (
                                                            <div className="delivery-logs-inline-success">
                                                                Feedback sent successfully!
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="delivery-logs-footer">
                    <button 
                        className="delivery-logs-close-button-footer" 
                        onClick={onClose}
                        type="button"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeliveryLogsModal;
